import { useState, useEffect, useMemo } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Users, CalendarIcon, AlertCircle, User, Edit, AlertTriangle, Clock, Baby, Palmtree, StickyNote, Pencil } from "lucide-react";
import { StrollerIcon } from "@/components/icons/StrollerIcon";
import { useCustomerNotes } from "@/hooks/useCustomerNotes";
import { supabase } from "@/integrations/supabase/client";
import { useEstablishment } from "@/contexts/EstablishmentContext";
import { groupTablesBySpace } from "@/lib/tableSpaceGrouping";
import { useEstablishmentQuestions } from "@/hooks/useEstablishmentQuestions";
import { selectApplicableQuestion, normalizeAnswers } from "@/lib/establishmentQuestionUtils";
import { EstablishmentQuestionField } from "@/components/EstablishmentQuestionField";
import { loadCustomTableOrder, sortByCustomOrder, resolveTableOrder } from "@/lib/tableOrderUtils";
import { TerracePreferenceSelector } from "@/components/TerracePreferenceSelector";
import type { TerracePreference } from "@/types/table";
import { Checkbox } from "@/components/ui/checkbox";
import { Table, TableAvailability, TableWeekdayAvailability, TableBookablePeriod } from "@/types/table";
import { CustomerData } from "@/types/client";
import { useToast } from "@/hooks/use-toast";
import { format, isToday, isBefore } from "date-fns";
import { fr } from "date-fns/locale";
import { cn } from "@/lib/utils";
import { formatCustomerName } from "@/lib/clientUtils";
import { isTableAvailableOnDate, isTableInTemporaryBlockingPeriod } from "@/lib/tableAvailabilityUtils";
import { getBlockedTableIds } from "@/lib/reservationConflictUtils";
import { CombinedTable } from "@/types/table";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Card, CardContent } from "@/components/ui/card";
import { EditClientDialog } from "./EditClientDialog";
import { normalizeTimeForUI } from "@/lib/timeFormatUtils";
import { generateAvailableTimes } from "@/lib/timeBookingUtils";
import { getTimeSlotsForDate, type TimeSlotDateOverride } from "@/lib/timeSlotUtils";
import { getStaffSlotsForDate } from "@/lib/zoneScheduleUtils";
import { useZoneTimeWindows } from "@/hooks/useZoneTimeWindows";
import { useBookableZones } from "@/hooks/useBookableZones";
import { TimeBookingGrid } from "@/components/TimeBookingGrid";

interface EditReservationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  reservation: {
    id: number;
    supabaseId?: string;
    customerId?: string;
    name: string;
    email?: string;
    phone: string;
    persons: number;
    numberOfChildren?: number;
    hasStroller?: boolean;
    tableId: string | undefined;
    combinedTableId: string | undefined;
    time: string;
    reservationDate: Date;
    notes?: string;
    internalNotes?: string;
    status?: "planifié" | "arrivé" | "annulé" | "en_attente";
    durationMinutes?: number;
    source?: string;
    customAnswers?: { question: string; answers: string[] };
    terracePreference?: 'no_preference' | 'terrace' | 'indoor';
  } | null;
  timeSlots: { start: string; end: string; availableDays?: number[]; durationOverrideMinutes?: number | null; }[];
  dateOverrides?: TimeSlotDateOverride[];
  allTables: Table[];
  allReservations: Array<{
    id: number;
    supabaseId?: string;
    tableId: string | undefined;
    combinedTableId: string | undefined;
    time: string;
    reservationDate: Date;
    durationMinutes?: number;
  }>;
  availabilities: TableAvailability[];
  weekdayAvailabilities: TableWeekdayAvailability[];
  combinedTables: CombinedTable[];
  existingClients: CustomerData[];
  onReservationUpdate: (reservationId: number, updates: {
    customerName: string;
    customerEmail?: string;
    customerPhone: string;
    numberOfPeople: number;
    numberOfChildren?: number;
    hasStroller?: boolean;
    tableId?: string;
    combinedTableId?: string;
    date: Date;
    startTime: string;
    notes?: string;
    internalNotes?: string;
    status?: "planifié" | "arrivé" | "annulé" | "en_attente";
    durationMinutes?: number;
    source?: string;
    customAnswers?: { question: string; answers: string[] } | null;
  }) => void;
  onClientUpdate: (oldEmail: string, updates: Partial<CustomerData>) => void;
  reservationSettings?: {
    bookingMode: 'slot' | 'time';
    bookingIntervalMinutes: 15 | 30 | 60;
    reservationDurationMinutes: number;
  };
  maxPeople?: number;
  childrenEnabled?: boolean;
  strollerEnabled?: boolean;
  childrenCapacityMode?: 'full' | 'half' | 'none';
  terraceEnabled?: boolean;
  tableBookablePeriods?: TableBookablePeriod[];
  establishmentClosures?: { start_date: string; end_date: string }[];
}

export function EditReservationDialog({
  open,
  onOpenChange,
  reservation,
  timeSlots,
  dateOverrides = [],
  allTables,
  allReservations,
  availabilities,
  weekdayAvailabilities,
  combinedTables,
  existingClients,
  onReservationUpdate,
  onClientUpdate,
  reservationSettings,
  maxPeople,
  childrenEnabled = false,
  strollerEnabled = false,
  childrenCapacityMode = 'full',
  terraceEnabled = false,
  tableBookablePeriods = [],
  establishmentClosures = [],
}: EditReservationDialogProps) {
  const { toast } = useToast();
  const { establishmentId: ctxEstablishmentId } = useEstablishment();
  const customTableOrder = useMemo(
    () => resolveTableOrder({ establishmentId: ctxEstablishmentId, tables: allTables, combinedTables }),
    [ctxEstablishmentId, allTables, combinedTables, open]
  );
  const { customerNotes, getNotesForEmail, getCustomerIdForEmail, refetch: refetchCustomerNotes } = useCustomerNotes();
  const [editingCustomerNotes, setEditingCustomerNotes] = useState(false);
  const [customerNotesValue, setCustomerNotesValue] = useState("");
  const [savingCustomerNotes, setSavingCustomerNotes] = useState(false);
  // Build conflict options based on reservation settings (TIME mode support)
  const isTimeMode = reservationSettings?.bookingMode === 'time';
  const bookingIntervalMinutes = reservationSettings?.bookingIntervalMinutes ?? 30;
  const reservationDurationMinutes = reservationSettings?.reservationDurationMinutes ?? 120;
  
  const conflictOptions = isTimeMode
    ? { 
        bookingMode: 'time' as const, 
        reservationDurationMinutes: reservationDurationMinutes 
      }
    : undefined;
  
  const [editingClient, setEditingClient] = useState(false);
  const [numberOfPeople, setNumberOfPeople] = useState("2");
  const [selectedTableId, setSelectedTableId] = useState("");
  const [selectedTableType, setSelectedTableType] = useState<'single' | 'combined'>('single');
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [selectedTimeSlot, setSelectedTimeSlot] = useState("");
  const [notes, setNotes] = useState("");
  const [internalNotes, setInternalNotes] = useState("");
  const [status, setStatus] = useState<"planifié" | "arrivé" | "annulé" | "en_attente">("planifié");
  const [numberOfChildren, setNumberOfChildren] = useState(0);
  const [hasStroller, setHasStroller] = useState(false);
  const [customDuration, setCustomDuration] = useState<number | null>(null);
  const [terracePreference, setTerracePreference] = useState<TerracePreference>('no_preference');

  // Question de l'établissement : réponse modifiable par l'équipe
  const { questions: establishmentQuestions } = useEstablishmentQuestions({
    establishmentId: ctxEstablishmentId,
  });
  const applicableQuestion = useMemo(
    () => selectApplicableQuestion(establishmentQuestions, selectedDate),
    [establishmentQuestions, selectedDate]
  );
  const [customAnswers, setCustomAnswers] = useState<string[]>([]);
  // La réponse enregistrée n'est modifiable que si la question affichée est bien
  // celle à laquelle le client a répondu (sinon on conserve la valeur d'origine).
  const answersBelongToApplicableQuestion =
    !!applicableQuestion &&
    (!reservation?.customAnswers?.question ||
      reservation.customAnswers.question === applicableQuestion.question);
  // Changement de question à l'écran (date déplacée) : on repart de l'existant si
  // la question correspond, sinon d'une réponse vide pour éviter toute confusion.
  useEffect(() => {
    if (!open) return;
    setCustomAnswers(
      answersBelongToApplicableQuestion ? reservation?.customAnswers?.answers ?? [] : []
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicableQuestion?.id, open]);


  // Derived variables for children/stroller filtering
  const hasChildrenActive = childrenEnabled && numberOfChildren > 0;
  const strollerActive = hasStroller && strollerEnabled;
  const [source, setSource] = useState("");

  // Auto-adjust children count when people count decreases
  useEffect(() => {
    const people = parseInt(numberOfPeople);
    if (numberOfChildren >= people) {
      setNumberOfChildren(Math.max(0, people - 1));
    }
  }, [numberOfPeople, numberOfChildren]);

  // Horaires propres aux salles (additifs côté équipe)
  const { zoneWindows } = useZoneTimeWindows({ establishmentId: ctxEstablishmentId, useAuthenticatedData: true });
  const { zones: editBookableZones } = useBookableZones({ establishmentId: ctxEstablishmentId, useAuthenticatedData: true });
  const activeZoneIds = useMemo(
    () => new Set(editBookableZones.filter(z => z.isActive).map(z => z.id)),
    [editBookableZones]
  );

  // Filter time slots by the dialog's own selected date (not the dashboard date)
  const filteredTimeSlots = useMemo(() => {
    if (!selectedDate) return timeSlots;
    const slotsForDate = getStaffSlotsForDate(selectedDate, timeSlots.map(ts => ({
      start: ts.start,
      end: ts.end,
      availableDays: ts.availableDays,
    })), dateOverrides, zoneWindows, activeZoneIds);
    return slotsForDate.map(ts => ({
      start: ts.start,
      end: ts.end,
      availableDays: ts.availableDays,
      durationOverrideMinutes: ts.durationOverrideMinutes,
    }));
  }, [timeSlots, dateOverrides, selectedDate, zoneWindows, activeZoneIds]);

  // Generate available booking times for TIME mode
  const availableBookingTimes = useMemo(() => {
    if (!isTimeMode) return [];
    
    const slotRanges = filteredTimeSlots.map(ts => ({ start: ts.start, end: ts.end, durationOverrideMinutes: ts.durationOverrideMinutes }));
    return generateAvailableTimes(slotRanges, bookingIntervalMinutes, reservationDurationMinutes);
  }, [isTimeMode, filteredTimeSlots, bookingIntervalMinutes, reservationDurationMinutes]);

  // Calculate TIME mode availability for each individual time
  const timeAvailabilityForEdit = useMemo(() => {
    if (!isTimeMode || !selectedDate || availableBookingTimes.length === 0 || !reservation) return [];

    const now = new Date();
    const personsCount = parseInt(numberOfPeople) || 0;

    return availableBookingTimes.map(time => {
      // Check if time is in the past for today
      if (isToday(selectedDate)) {
        const [hours, minutes] = time.split(':').map(Number);
        const bookingTime = new Date(selectedDate);
        bookingTime.setHours(hours, minutes, 0, 0);
        if (isBefore(bookingTime, now)) {
          return { time, isAvailable: false, unavailableReason: 'hours' as const };
        }
      }

      // Convert reservations for conflict check (excluding current reservation)
      const convertedReservations = allReservations
        .filter(r => reservation.supabaseId ? r.supabaseId !== reservation.supabaseId : r.id !== reservation.id)
        .map(r => ({
          id: r.id.toString(),
          tableId: r.tableId,
          combinedTableId: r.combinedTableId,
          customerFirstName: '',
          customerLastName: '',
          customerPhone: '',
          numberOfPeople: 0,
          date: r.reservationDate,
          startTime: r.time,
          status: (r as any).status || 'planifié',
          durationMinutes: r.durationMinutes,
          createdAt: new Date(),
        }));

      const blockedIds = getBlockedTableIds(
        convertedReservations,
        combinedTables,
        selectedDate,
        time,
        conflictOptions
      );

      // Available single tables for this time
      const availableSingleTables = allTables.filter(table => {
        if (blockedIds.has(table.id)) return false;
        if (hasChildrenActive && table.allowsChildren === false) return false;
        if (strollerActive && table.allowsStroller === false) return false;
        if (isTableInTemporaryBlockingPeriod(table.id, selectedDate, tableBookablePeriods)) return false;
        const weekdayAvailability = weekdayAvailabilities.find(w => w.tableId === table.id);
        if (!isTableAvailableOnDate(table, selectedDate, availabilities, weekdayAvailability || null)) return false;
        return true;
      });

      // Available combined tables for this time (sans filtre capacité - bypass manager)
      const availableCombined = combinedTables.filter(ct => {
        if (blockedIds.has(ct.id) || !ct.isActive) return false;
        if (hasChildrenActive && ct.allowsChildren === false) return false;
        if (strollerActive && ct.allowsStroller === false) return false;
        // Note: combinées hors réservation (isBookable=false) gardées pour assignation manuelle staff
        return ct.tableIds.every(tid => {
          const table = allTables.find(t => t.id === tid);
          if (!table) return false;
          if (hasChildrenActive && table.allowsChildren === false) return false;
          if (strollerActive && ct.strollerPolicy !== 'allow' && table.allowsStroller === false) return false;
          if (isTableInTemporaryBlockingPeriod(tid, selectedDate, tableBookablePeriods)) return false;
          const weekdayAvail = weekdayAvailabilities.find(w => w.tableId === tid);
          return isTableAvailableOnDate(table, selectedDate, availabilities, weekdayAvail || null);
        });
      });

      const isAvailable = availableSingleTables.length > 0 || availableCombined.length > 0;
      
      return {
        time,
        isAvailable,
        unavailableReason: isAvailable ? undefined : 'full' as const
      };
    });
  }, [isTimeMode, selectedDate, availableBookingTimes, numberOfPeople, allReservations, allTables, combinedTables, availabilities, weekdayAvailabilities, conflictOptions, reservation, hasChildrenActive, strollerActive, tableBookablePeriods]);

  // Get available tables for the selected date and time slot (includes both single and combined tables)
  const getAvailableTablesForDateTime = useMemo(() => {
    if (!selectedDate || !selectedTimeSlot || !reservation) return [];

    const personsCount = parseInt(numberOfPeople);

    // Filter single tables available on the selected date AND with sufficient capacity
    const tablesAvailableOnDate = allTables.filter(table => {
      const weekdayAvailability = weekdayAvailabilities.find(w => w.tableId === table.id);
      if (!isTableAvailableOnDate(table, selectedDate, availabilities, weekdayAvailability || null)) return false;
      if (isTableInTemporaryBlockingPeriod(table.id, selectedDate, tableBookablePeriods)) return false;
      return true;
    });

    // Convert allReservations to Reservation format for getBlockedTableIds
    const convertedReservations = allReservations
      .filter(r => reservation.supabaseId ? r.supabaseId !== reservation.supabaseId : r.id !== reservation.id) // Exclude the reservation being edited
      .map(r => ({
        id: r.id.toString(),
        tableId: r.tableId,
        combinedTableId: r.combinedTableId,
        customerFirstName: '',
        customerLastName: '',
        customerPhone: '',
        numberOfPeople: 0,
        date: r.reservationDate,
        startTime: r.time,
        status: (r as any).status || 'planifié',
        durationMinutes: r.durationMinutes,
        createdAt: new Date(),
      }));

    // Supports TIME mode when reservationSettings is provided
    // Use centralized function that automatically filters out cancellations
    const blockedIds = getBlockedTableIds(
      convertedReservations,
      combinedTables,
      selectedDate,
      selectedTimeSlot,
      conflictOptions
    );

    // Filter available single tables (not blocked OR is current reservation's table)
    const availableSingleTables = tablesAvailableOnDate
      .filter(table => {
        if (blockedIds.has(table.id) && table.id !== reservation.tableId) return false;
        if (hasChildrenActive && table.allowsChildren === false) return false;
        if (strollerActive && table.allowsStroller === false) return false;
        return true;
      })
      .map(table => ({ ...table, type: 'single' as const }));

    // Filter available combined tables
    const availableCombinedTables = combinedTables
      .filter(ct => {
        // Always include the current reservation's combined table
        if (ct.id === reservation.combinedTableId) return true;
        
        // Check if active
        if (!ct.isActive) return false;
        // Note: combinées hors réservation (isBookable=false) gardées pour assignation manuelle staff
        
        // Check if combined table itself is blocked (OR is current reservation's table)
        if (blockedIds.has(ct.id) && ct.id !== reservation.combinedTableId) return false;

        // Children/stroller filters
        if (hasChildrenActive && ct.allowsChildren === false) return false;
        if (strollerActive && ct.allowsStroller === false) return false;
        
        // Check if all component tables are available on the selected date
        const allComponentTablesAvailable = ct.tableIds.every(tid => {
          const componentTable = allTables.find(t => t.id === tid);
          if (!componentTable) return false;
          
          // Check if component table is blocked
          if (blockedIds.has(tid) && tid !== reservation.tableId) return false;

          if (hasChildrenActive && componentTable.allowsChildren === false) return false;
          if (strollerActive && ct.strollerPolicy !== 'allow' && componentTable.allowsStroller === false) return false;
          
          // Check temporary blocking periods for sub-tables
          if (isTableInTemporaryBlockingPeriod(tid, selectedDate, tableBookablePeriods)) return false;
          
          const weekdayAvail = weekdayAvailabilities.find(w => w.tableId === tid);
          return isTableAvailableOnDate(componentTable, selectedDate, availabilities, weekdayAvail || null);
        });
        
        return allComponentTablesAvailable;
      })
      .map(ct => ({ 
        id: ct.id, 
        name: ct.name, 
        capacity: ct.totalCapacity, 
        isActive: ct.isActive,
        isBookable: ct.isBookable !== false,
        notes: `Combinaison de ${ct.tableIds.length} tables`,
        createdAt: ct.createdAt,
        isTerrace: ct.isTerrace === true,
        zoneId: ct.zoneId ?? null,
        type: 'combined' as const
      }));

    // Merge then apply Timeline custom order (fallback: capacity ASC)
    return sortByCustomOrder(
      [...availableSingleTables, ...availableCombinedTables],
      customTableOrder,
      (a, b) => a.capacity - b.capacity
    );
  }, [selectedDate, selectedTimeSlot, numberOfPeople, allTables, allReservations, availabilities, weekdayAvailabilities, combinedTables, reservation, hasChildrenActive, strollerActive, tableBookablePeriods, customTableOrder]);

  // Get available time slots for the selected date
  const availableTimeSlots = useMemo(() => {
    if (!selectedDate || !reservation) return [];
    
    const personsCount = parseInt(numberOfPeople) || 0;

    return filteredTimeSlots.filter(slot => {
      const tablesAvailableOnDate = allTables.filter(table => {
        const weekdayAvailability = weekdayAvailabilities.find(w => w.tableId === table.id);
        if (!isTableAvailableOnDate(table, selectedDate, availabilities, weekdayAvailability || null)) return false;
        if (isTableInTemporaryBlockingPeriod(table.id, selectedDate, tableBookablePeriods)) return false;
        return true;
      });

      const convertedReservations = allReservations
        .filter(r => reservation.supabaseId ? r.supabaseId !== reservation.supabaseId : r.id !== reservation.id)
        .map(r => ({
          id: r.id.toString(),
          tableId: r.tableId,
          combinedTableId: r.combinedTableId,
          customerFirstName: '',
          customerLastName: '',
          customerPhone: '',
          numberOfPeople: 0,
          date: r.reservationDate,
          startTime: r.time,
          status: (r as any).status || 'planifié',
          durationMinutes: r.durationMinutes,
          createdAt: new Date(),
        }));

      // Supports TIME mode when reservationSettings is provided
      const blockedIds = getBlockedTableIds(
        convertedReservations,
        combinedTables,
        selectedDate,
        slot.start,
        conflictOptions
      );

      // Filtrer les tables disponibles (sans filtre de capacité - bypass manager)
      const availableTables = tablesAvailableOnDate.filter(table => {
        if (blockedIds.has(table.id)) return false;
        if (hasChildrenActive && table.allowsChildren === false) return false;
        if (strollerActive && table.allowsStroller === false) return false;
        return true;
      });
      
      // Vérifier les tables combinées disponibles (sans filtre de capacité - bypass manager)
      const availableCombined = combinedTables.filter(ct => {
        if (blockedIds.has(ct.id) || !ct.isActive) return false;
        // Note: combinées hors réservation (isBookable=false) gardées pour assignation manuelle staff
        if (hasChildrenActive && ct.allowsChildren === false) return false;
        if (strollerActive && ct.allowsStroller === false) return false;
        return ct.tableIds.every(tid => {
          const table = allTables.find(t => t.id === tid);
          if (!table) return false;
          if (hasChildrenActive && table.allowsChildren === false) return false;
          if (strollerActive && ct.strollerPolicy !== 'allow' && table.allowsStroller === false) return false;
          if (isTableInTemporaryBlockingPeriod(tid, selectedDate, tableBookablePeriods)) return false;
          const weekdayAvail = weekdayAvailabilities.find(w => w.tableId === tid);
          return isTableAvailableOnDate(table, selectedDate, availabilities, weekdayAvail || null);
        });
      });

      // Créneau disponible SI au moins une table simple OU combinée avec capacité suffisante
      return availableTables.length > 0 || availableCombined.length > 0 || slot.start === reservation.time;
    });
  }, [selectedDate, timeSlots, allTables, allReservations, availabilities, weekdayAvailabilities, reservation, numberOfPeople, combinedTables, hasChildrenActive, strollerActive, tableBookablePeriods]);

  // Nombre max sélectionnable par l'équipe : plus grande capacité de l'établissement
  // (tables simples ou combinées), plancher à 20, valeur courante toujours sélectionnable.
  const maxSelectablePeople = useMemo(() => {
    const maxSingle = allTables.length > 0 ? Math.max(...allTables.map(t => t.capacity)) : 0;
    const maxCombined = combinedTables.length > 0 ? Math.max(...combinedTables.map(ct => ct.totalCapacity)) : 0;
    const current = parseInt(numberOfPeople || '0') || 0;
    return Math.min(100, Math.max(20, maxSingle, maxCombined, current));
  }, [allTables, combinedTables, numberOfPeople]);

  // Calculate available person counts for selected date and time slot
  const availablePersonCounts = useMemo(() => {
    if (!selectedDate || !selectedTimeSlot) {
      return Array.from({ length: maxSelectablePeople }, (_, i) => ({ count: i + 1, isAvailable: true, hasMatchingTable: true }));
    }

    // Convertir les réservations en excluant celle en cours d'édition
    const convertedReservations = allReservations
      .filter(r => reservation?.supabaseId ? r.supabaseId !== reservation.supabaseId : r.id !== reservation?.id)
      .map(r => ({
        id: r.id.toString(),
        tableId: r.tableId,
        combinedTableId: r.combinedTableId,
        customerFirstName: '',
        customerLastName: '',
        customerPhone: '',
        numberOfPeople: 0,
        date: r.reservationDate,
        startTime: r.time,
        status: (r as any).status || 'planifié',
        durationMinutes: r.durationMinutes,
        createdAt: new Date(),
      }));

    // Supports TIME mode when reservationSettings is provided
    const blockedIds = getBlockedTableIds(
      convertedReservations,
      combinedTables,
      selectedDate,
      selectedTimeSlot,
      conflictOptions
    );

    // Tables disponibles pour ce créneau
    const availableTables = allTables.filter(table => {
      if (blockedIds.has(table.id) && table.id !== reservation?.tableId) return false;
      if (hasChildrenActive && table.allowsChildren === false) return false;
      if (strollerActive && table.allowsStroller === false) return false;
      if (isTableInTemporaryBlockingPeriod(table.id, selectedDate, tableBookablePeriods)) return false;
      const weekdayAvailability = weekdayAvailabilities.find(w => w.tableId === table.id);
      return isTableAvailableOnDate(table, selectedDate, availabilities, weekdayAvailability || null);
    });

    // Combinaisons disponibles
    const availableCombined = combinedTables.filter(ct => {
      if (blockedIds.has(ct.id) || !ct.isActive) return false;
      // Note: combinées hors réservation (isBookable=false) gardées pour assignation manuelle staff
      if (hasChildrenActive && ct.allowsChildren === false) return false;
      if (strollerActive && ct.allowsStroller === false) return false;
      return ct.tableIds.every(tid => {
        const table = availableTables.find(t => t.id === tid);
        if (!table) return false;
        if (hasChildrenActive && table.allowsChildren === false) return false;
        if (strollerActive && ct.strollerPolicy !== 'allow' && table.allowsStroller === false) return false;
        if (isTableInTemporaryBlockingPeriod(tid, selectedDate, tableBookablePeriods)) return false;
        const weekdayAvail = weekdayAvailabilities.find(w => w.tableId === tid);
        return isTableAvailableOnDate(table, selectedDate, availabilities, weekdayAvail || null);
      });
    });

    const maxSingleCapacity = availableTables.length > 0 
      ? Math.max(...availableTables.map(t => t.capacity)) 
      : 0;
    const maxCombinedCapacity = availableCombined.length > 0 
      ? Math.max(...availableCombined.map(ct => ct.totalCapacity)) 
      : 0;
    const maxCapacity = Math.max(maxSingleCapacity, maxCombinedCapacity);

    return Array.from({ length: maxSelectablePeople }, (_, i) => {
      const count = i + 1;
      const hasMatchingTable = count <= maxCapacity;
      return { count, isAvailable: true, hasMatchingTable };
    });
  }, [selectedDate, selectedTimeSlot, allTables, allReservations, availabilities, weekdayAvailabilities, combinedTables, reservation, hasChildrenActive, strollerActive, tableBookablePeriods, maxSelectablePeople]);

  // Find the client associated with this reservation
  const reservationClient = useMemo(() => {
    if (!reservation) return null;
    // Try to find client by email first
    if (reservation.email) {
      const found = existingClients.find(client => client.email === reservation.email);
      if (found) return found;
    }
    // Fallback: build a client object from reservation data (handles no-email clients)
    return {
      id: reservation.customerId,
      firstName: reservation.name?.split(' ')[0] || '',
      lastName: reservation.name?.split(' ').slice(1).join(' ') || '',
      email: reservation.email || '',
      phone: reservation.phone || '',
      totalReservations: 0,
      totalPersons: 0,
      lastReservationDate: reservation.reservationDate || new Date(),
      firstReservationDate: reservation.reservationDate || new Date(),
      allReservations: [],
    } as CustomerData;
  }, [reservation, existingClients]);

  // Initialize form with reservation data when dialog opens
  useEffect(() => {
    if (reservation && open) {
      const total = (reservation.persons || 0) + (reservation.numberOfChildren || 0);
      setNumberOfPeople(total > 0 ? total.toString() : "2");
      setSelectedDate(reservation.reservationDate);
      setSelectedTimeSlot(normalizeTimeForUI(reservation.time));
      
      // Déterminer le type de table et l'ID - avec un léger délai pour s'assurer que les tables sont calculées
      setTimeout(() => {
        if (reservation.combinedTableId) {
          setSelectedTableId(reservation.combinedTableId);
          setSelectedTableType('combined');
        } else if (reservation.tableId) {
          setSelectedTableId(reservation.tableId);
          setSelectedTableType('single');
        } else {
          setSelectedTableId("");
          setSelectedTableType('single');
        }
      }, 0);
      
      setNotes(reservation.notes || "");
      setInternalNotes(reservation.internalNotes || "");
      setStatus(reservation.status || "planifié");
      setNumberOfChildren(reservation.numberOfChildren || 0);
      setHasStroller(reservation.hasStroller || false);
      setCustomDuration(reservation.durationMinutes ?? null);
      setSource(reservation.source || "");
      setTerracePreference(reservation.terracePreference || 'no_preference');
      setCustomAnswers(reservation.customAnswers?.answers ?? []);
    }
  }, [reservation, open]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!reservation || !reservationClient) {
      toast({
        title: "Erreur",
        description: "Impossible de trouver le client associé à cette réservation.",
        variant: "destructive",
      });
      return;
    }

    if (!selectedTableId) {
      toast({
        title: "Table requise",
        description: "Veuillez sélectionner une table avant d'enregistrer.",
        variant: "destructive",
      });
      return;
    }



    // Auto-transition: si en_attente et une table vient d'être assignée, passer à planifié
    let finalStatus = status;
    if (status === 'en_attente' && selectedTableId) {
      const hadNoTable = !reservation.tableId && !reservation.combinedTableId;
      if (hadNoTable) {
        finalStatus = 'planifié';
        toast({
          title: "Statut mis à jour",
          description: "La réservation est passée de « En attente » à « Planifié » suite à l'assignation d'une table.",
        });
      }
    }

    onReservationUpdate(reservation.id, {
      customerName: formatCustomerName(reservationClient.firstName, reservationClient.lastName),
      customerEmail: reservationClient.email,
      customerPhone: reservationClient.phone,
      numberOfPeople: childrenEnabled ? parseInt(numberOfPeople) - numberOfChildren : parseInt(numberOfPeople),
      numberOfChildren,
      hasStroller,
      terracePreference: terraceEnabled ? terracePreference : undefined,
      tableId: selectedTableType === 'single' ? selectedTableId : undefined,
      combinedTableId: selectedTableType === 'combined' ? selectedTableId : undefined,
      date: selectedDate,
      startTime: selectedTimeSlot,
      notes: notes.trim(),
      internalNotes: internalNotes.trim(),
      status: finalStatus,
      durationMinutes: customDuration || undefined,
      source: source || undefined,
      ...(applicableQuestion && answersBelongToApplicableQuestion
        ? {
            customAnswers:
              normalizeAnswers(applicableQuestion, customAnswers).length > 0
                ? {
                    question: applicableQuestion.question,
                    answers: normalizeAnswers(applicableQuestion, customAnswers),
                  }
                : null,
          }
        : {}),
      _supabaseId: reservation.supabaseId,
    } as any);

    onOpenChange(false);
  };

  // Check if current table is still available after date/time change
  const isCurrentTableUnavailable = useMemo(() => {
    if (!reservation || !selectedTableId) return false;
    
    // Si on n'a pas changé date ni heure, la table reste valide
    if (selectedDate.getTime() === reservation.reservationDate.getTime() && 
        selectedTimeSlot === normalizeTimeForUI(reservation.time)) {
      return false;
    }
    
    // Vérifier si la table sélectionnée est toujours dans la liste des disponibles
    return !getAvailableTablesForDateTime.some(t => t.id === selectedTableId);
  }, [reservation, selectedTableId, selectedDate, selectedTimeSlot, getAvailableTablesForDateTime]);

  // Reset table selection if it becomes unavailable + show toast
  // But first check if the original reservation table is still free at the new slot
  useEffect(() => {
    if (isCurrentTableUnavailable && selectedTableId && reservation) {
      // Check if original table from the reservation is still available
      const originalTableId = reservation.tableId || reservation.combinedTableId;
      const originalStillAvailable = originalTableId && getAvailableTablesForDateTime.some(t => t.id === originalTableId);
      
      if (originalStillAvailable && originalTableId) {
        // Keep the original table selected instead of clearing
        setSelectedTableId(originalTableId);
        const isOriginalCombined = combinedTables.some(ct => ct.id === originalTableId);
        setSelectedTableType(isOriginalCombined ? 'combined' : 'single');
      } else {
        const currentTableName = allTables.find(t => t.id === selectedTableId)?.name || 
                                 combinedTables.find(ct => ct.id === selectedTableId)?.name || 
                                 "sélectionnée";
        
        toast({
          title: "Table indisponible",
          description: `La table "${currentTableName}" n'est plus disponible pour ce créneau. Veuillez en sélectionner une autre.`,
          variant: "destructive",
        });
        
        setSelectedTableId("");
      }
    }
  }, [isCurrentTableUnavailable, selectedTableId, allTables, combinedTables, toast, reservation, getAvailableTablesForDateTime]);

  // Reset time slot if no tables available for new number of people
  useEffect(() => {
    if (selectedTimeSlot && numberOfPeople && reservation) {
      const personsCount = parseInt(numberOfPeople);
      if (getAvailableTablesForDateTime.length === 0 && selectedTimeSlot !== reservation.time) {
        toast({
          title: "Créneau indisponible",
          description: `Aucune table disponible pour ${personsCount} personne(s) à ${selectedTimeSlot}. Veuillez choisir un autre créneau.`,
          variant: "destructive",
        });
        setSelectedTimeSlot("");
        setSelectedTableId("");
      }
    }
  }, [numberOfPeople, selectedTimeSlot, getAvailableTablesForDateTime, reservation, toast]);

  if (!reservation) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">
        <DialogHeader className="shrink-0 border-b pb-4">
          <DialogTitle>
            Modifier la réservation
          </DialogTitle>
          <DialogDescription asChild>
            <div className="space-y-2 pt-1">
              {/* Client identity */}
              <div className="flex items-center gap-2">
                <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                  <User className="h-3.5 w-3.5" />
                </div>
                <span className="font-semibold text-foreground text-sm">
                  {reservation.name || "Client inconnu"}
                </span>
              </div>
              {/* Context line */}
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                <span className="flex items-center gap-1">
                  <CalendarIcon className="h-3 w-3" />
                  {format(reservation.reservationDate, "d MMM yyyy", { locale: fr })}
                </span>
                <span className="flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  {normalizeTimeForUI(reservation.time)}
                </span>
                {(() => {
                  const tableName = reservation.combinedTableId
                    ? combinedTables.find(ct => ct.id === reservation.combinedTableId)?.name
                    : allTables.find(t => t.id === reservation.tableId)?.name;
                  return tableName ? (
                    <span className="flex items-center gap-1">
                      <span>·</span> {tableName}
                    </span>
                  ) : null;
                })()}
                <Badge
                  variant={
                    status === "arrivé" ? "success" :
                    status === "annulé" ? "destructive" :
                    "info"
                  }
                  className="text-[10px] px-1.5 py-0"
                >
                  {status.charAt(0).toUpperCase() + status.slice(1)}
                </Badge>
              </div>
            </div>
          </DialogDescription>
        </DialogHeader>

        <form id="edit-reservation-form" onSubmit={handleSubmit} className="flex-1 overflow-y-auto min-h-0 px-1">
          <div className="space-y-6 pt-4">
            {/* Terrace preference */}
            {terraceEnabled && (
              <TerracePreferenceSelector
                value={terracePreference}
                onChange={setTerracePreference}
                compact
              />
            )}
            {!terraceEnabled && reservation?.terracePreference && reservation.terracePreference !== 'no_preference' && (
              <div className="flex items-center gap-2">
                <Palmtree className="h-4 w-4 text-primary" />
                <Badge variant="outline" className="text-xs">
                  {reservation.terracePreference === 'terrace' ? '🌿 Préférence terrasse' : '🏠 Préférence intérieur'}
                </Badge>
              </div>
            )}

            {/* Past date warning */}
            {(() => {
              const today = new Date();
              today.setHours(0, 0, 0, 0);
              return selectedDate < today;
            })() && (
              <div className="flex items-center gap-2 p-3 rounded-lg border border-orange-200 bg-orange-50 text-orange-800 dark:border-orange-800 dark:bg-orange-950/30 dark:text-orange-300 text-sm">
                <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                <span>Cette réservation est dans le passé.</span>
              </div>
            )}

            {/* Date and Time Slot Section */}
            <div className="space-y-4 p-4 bg-muted/30 rounded-lg">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Date *</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal",
                          !selectedDate && "text-muted-foreground"
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {selectedDate ? format(selectedDate, "PPP", { locale: fr }) : <span>Choisir une date</span>}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={selectedDate}
                        onSelect={(date) => date && setSelectedDate(date)}
                        initialFocus
                        className={cn("p-3 pointer-events-auto")}
                        disabled={(date) => {
                          const dateStr = format(date, "yyyy-MM-dd");
                          return establishmentClosures.some(
                            c => dateStr >= c.start_date && dateStr <= c.end_date
                          );
                        }}
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="timeSlot">
                    {isTimeMode ? 'Heure de réservation *' : 'Créneau horaire *'}
                  </Label>
                  {isTimeMode ? (
                    <div className="pt-1">
                      <TimeBookingGrid
                        availableTimes={timeAvailabilityForEdit}
                        selectedTime={selectedTimeSlot}
                        onTimeSelect={(time) => {
                          setSelectedTimeSlot(time);
                          // Ne PAS vider selectedTableId ici.
                          // Le useEffect "isCurrentTableUnavailable" gère déjà
                          // la préservation ou le reset de la table.
                        }}
                      />
                    </div>
                  ) : (
                    <Select value={selectedTimeSlot} onValueChange={setSelectedTimeSlot}>
                      <SelectTrigger id="timeSlot">
                        <SelectValue placeholder="Sélectionner un créneau" />
                      </SelectTrigger>
                      <SelectContent>
                        {availableTimeSlots.length === 0 ? (
                          <SelectItem value="no-slots" disabled>
                            Aucun créneau disponible pour {numberOfPeople} personne(s)
                          </SelectItem>
                        ) : (
                          availableTimeSlots.map((slot) => {
                            const personsCount = parseInt(numberOfPeople) || 0;
                            
                            // Calculer les tables disponibles pour ce créneau spécifique
                            const convertedReservations = allReservations
              .filter(r => reservation?.supabaseId 
                ? r.supabaseId !== reservation.supabaseId 
                : r.id !== reservation?.id)
                              .map(r => ({
                                id: r.id.toString(),
                                tableId: r.tableId,
                                combinedTableId: r.combinedTableId,
                                customerFirstName: '',
                                customerLastName: '',
                                customerPhone: '',
                                numberOfPeople: 0,
                                date: r.reservationDate,
                                startTime: r.time,
                                status: (r as any).status || 'planifié',
                                durationMinutes: r.durationMinutes,
                                createdAt: new Date(),
                              }));

                            const blockedIds = getBlockedTableIds(
                              convertedReservations,
                              combinedTables,
                              selectedDate,
                              slot.start,
                              conflictOptions
                            );

                            const availableTablesForSlot = allTables.filter(table => {
                              if (blockedIds.has(table.id)) return false;
                              const weekdayAvailability = weekdayAvailabilities.find(w => w.tableId === table.id);
                              return isTableAvailableOnDate(table, selectedDate, availabilities, weekdayAvailability || null);
                            });
                            
                            const availableCombinedForSlot = combinedTables.filter(ct => {
                              if (blockedIds.has(ct.id) || !ct.isActive) return false;
                              return ct.tableIds.every(tid => {
                                const table = allTables.find(t => t.id === tid);
                                if (!table) return false;
                                const weekdayAvail = weekdayAvailabilities.find(w => w.tableId === tid);
                                return isTableAvailableOnDate(table, selectedDate, availabilities, weekdayAvail || null);
                              });
                            });

                            const totalAvailable = availableTablesForSlot.length + availableCombinedForSlot.length;

                            return (
                              <SelectItem key={slot.start} value={slot.start}>
                                <div className="flex items-center justify-between w-full gap-4">
                                  <span>{slot.start} - {slot.end}</span>
                                  <span className={cn(
                                    "text-xs font-medium",
                                    totalAvailable > 0 ? "text-green-600" : "text-red-600"
                                  )}>
                                    {totalAvailable} table{totalAvailable > 1 ? 's' : ''} ({personsCount} pers.)
                                  </span>
                                </div>
                              </SelectItem>
                            );
                          })
                        )}
                      </SelectContent>
                    </Select>
                  )}
                </div>
              </div>

              {isCurrentTableUnavailable && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    La table actuellement sélectionnée n'est plus disponible pour ce créneau. Veuillez en choisir une autre.
                  </AlertDescription>
                </Alert>
              )}


              {getAvailableTablesForDateTime.length === 0 && selectedTimeSlot && selectedDate && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    <strong>Aucune table disponible</strong> pour cette date et ce créneau horaire avec {numberOfPeople} personne(s).
                    <br />
                    <span className="text-sm">Suggestions : choisir une autre date, un autre horaire, ou réduire le nombre de personnes.</span>
                  </AlertDescription>
                </Alert>
              )}
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Label htmlFor="persons">Nombre de personnes {childrenEnabled && "(enfants inclus)"} *</Label>
                  {maxPeople && parseInt(numberOfPeople || '0') > maxPeople && (
                    <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950 dark:text-amber-300 dark:border-amber-800">
                      <AlertTriangle className="w-3 h-3 mr-1" />
                      Dépasse la limite publique ({maxPeople})
                    </Badge>
                  )}
                </div>
                <Select value={numberOfPeople} onValueChange={(value) => {
                  const newPersons = parseInt(value);
                  const oldPersons = parseInt(numberOfPeople);
                  
                  setNumberOfPeople(value);
                  
                  // Si augmentation et table actuelle insuffisante
                  if (selectedTableId && newPersons > oldPersons) {
                    const currentSingleTable = allTables.find(t => t.id === selectedTableId);
                    const currentCombinedTable = combinedTables.find(ct => ct.id === selectedTableId);
                    
                    const currentCapacity = currentSingleTable?.capacity || currentCombinedTable?.totalCapacity || 0;
                    const tableName = currentSingleTable?.name || currentCombinedTable?.name || "sélectionnée";
                    
                    if (currentCapacity < newPersons) {
                      toast({
                        title: "Sous-capacité",
                        description: `La table "${tableName}" (${currentCapacity} pers.) est en sous-capacité pour ${newPersons} personnes.`,
                      });
                    }
                  }
                  
                  // Vérifier si le créneau actuel a encore des tables disponibles
                  if (selectedTimeSlot && reservation) {
                    const convertedReservations = allReservations
                      .filter(r => reservation.supabaseId ? r.supabaseId !== reservation.supabaseId : r.id !== reservation.id)
                      .map(r => ({
                        id: r.id.toString(),
                        tableId: r.tableId,
                        combinedTableId: r.combinedTableId,
                        customerFirstName: '',
                        customerLastName: '',
                        customerPhone: '',
                        numberOfPeople: 0,
                        date: r.reservationDate,
                        startTime: r.time,
                        status: (r as any).status || 'planifié',
                        durationMinutes: r.durationMinutes,
                        createdAt: new Date(),
                      }));

                    const blockedIds = getBlockedTableIds(
                      convertedReservations,
                      combinedTables,
                      selectedDate,
                      selectedTimeSlot,
                      conflictOptions
                    );

                    const availableTablesForSlot = allTables.filter(table => {
                      if (blockedIds.has(table.id)) return false;
                      const weekdayAvailability = weekdayAvailabilities.find(w => w.tableId === table.id);
                      return isTableAvailableOnDate(table, selectedDate, availabilities, weekdayAvailability || null);
                    });
                    
                    const availableCombinedForSlot = combinedTables.filter(ct => {
                      if (blockedIds.has(ct.id) || !ct.isActive) return false;
                      return ct.tableIds.every(tid => {
                        const table = allTables.find(t => t.id === tid);
                        if (!table) return false;
                        const weekdayAvail = weekdayAvailabilities.find(w => w.tableId === tid);
                        return isTableAvailableOnDate(table, selectedDate, availabilities, weekdayAvail || null);
                      });
                    });
                    
                    if (availableTablesForSlot.length === 0 && availableCombinedForSlot.length === 0 && selectedTimeSlot !== reservation.time) {
                      toast({
                        title: "Créneau indisponible",
                        description: `Aucune table disponible pour ${newPersons} personne(s) à ${selectedTimeSlot}. Veuillez choisir un autre créneau.`,
                        variant: "destructive",
                      });
                      setSelectedTimeSlot("");
                      setSelectedTableId("");
                    }
                  }
                }}>
                  <SelectTrigger id="persons">
                    <SelectValue>
                      {numberOfPeople ? (
                        <div className="flex items-center gap-2">
                          <Users className="w-4 h-4" />
                          {numberOfPeople} personne{parseInt(numberOfPeople) > 1 ? "s" : ""}
                        </div>
                      ) : (
                        "Sélectionner le nombre de personnes"
                      )}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {availablePersonCounts.map(({ count, isAvailable, hasMatchingTable }) => (
                      <SelectItem 
                        key={count} 
                        value={count.toString()}
                      >
                        <div className="flex items-center gap-2">
                          <Users className="w-4 h-4" />
                          {count} personne{count > 1 ? "s" : ""}
                          {!hasMatchingTable && (
                            <span className="text-xs text-amber-600 dark:text-amber-400 ml-2">(aucune table adaptée)</span>
                          )}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Children selector */}
              {childrenEnabled && (<div className="space-y-2">
                <Label className="flex items-center gap-1.5">
                  <Baby className="w-4 h-4" />
                  Dont enfants
                </Label>
                <div className="flex items-center gap-2">
                  <Button type="button" variant="outline" size="icon" className="h-8 w-8" disabled={numberOfChildren <= 0} onClick={() => setNumberOfChildren(Math.max(0, numberOfChildren - 1))}>
                    <span className="text-lg">−</span>
                  </Button>
                  <span className="w-8 text-center font-medium text-sm">{numberOfChildren}</span>
                  <Button type="button" variant="outline" size="icon" className="h-8 w-8" disabled={numberOfChildren >= parseInt(numberOfPeople) - 1} onClick={() => setNumberOfChildren(Math.min(parseInt(numberOfPeople) - 1, numberOfChildren + 1))}>
                    <span className="text-lg">+</span>
                  </Button>
                </div>
                {numberOfChildren > 0 && (
                  <p className="text-xs text-muted-foreground">
                    Sur {numberOfPeople} personne{parseInt(numberOfPeople) > 1 ? "s" : ""}, {parseInt(numberOfPeople) - numberOfChildren} adulte{parseInt(numberOfPeople) - numberOfChildren > 1 ? "s" : ""} et {numberOfChildren} enfant{numberOfChildren > 1 ? "s" : ""}
                  </p>
                )}
              </div>)}

              {/* Stroller checkbox */}
              {strollerEnabled && (
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="editHasStroller"
                    checked={hasStroller}
                    onCheckedChange={(checked) => setHasStroller(checked === true)}
                  />
                  <Label htmlFor="editHasStroller" className="text-sm font-normal cursor-pointer flex items-center gap-1.5">
                    <StrollerIcon size={14} /> Poussette
                  </Label>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="table">Table *</Label>
                  <Select value={selectedTableId} onValueChange={(value) => {
                    setSelectedTableId(value);
                    // Détecter automatiquement si c'est une table combinée
                    const isCombined = combinedTables.some(ct => ct.id === value);
                    setSelectedTableType(isCombined ? 'combined' : 'single');
                  }}>
                    <SelectTrigger id="table">
                      <SelectValue placeholder="Sélectionner une table">
                        {selectedTableId && (() => {
                          // Chercher d'abord dans les tables disponibles
                          const availableTable = getAvailableTablesForDateTime.find(t => t.id === selectedTableId);
                          if (availableTable) {
                            return (
                              <div className="flex items-center gap-2">
                                <span>{availableTable.name} - {availableTable.capacity} pers.</span>
                                {availableTable.type === 'combined' && (
                                  <Badge variant="secondary" className="text-xs">Combinée</Badge>
                                )}
                              </div>
                            );
                          }
                          
                          // Sinon, chercher dans toutes les tables (peut être indisponible)
                          const singleTable = allTables.find(t => t.id === selectedTableId);
                          const combinedTable = combinedTables.find(ct => ct.id === selectedTableId);
                          const tableName = singleTable?.name || combinedTable?.name || "Table inconnue";
                          const capacity = singleTable?.capacity || combinedTable?.totalCapacity || 0;
                          const isCombined = !!combinedTable;
                          
                          return (
                            <div className="flex items-center gap-2">
                              <span className={isCurrentTableUnavailable ? "text-destructive" : ""}>
                                {isCurrentTableUnavailable && "⚠️ "}{tableName} - {capacity} pers.
                              </span>
                              {isCombined && (
                                <Badge variant={isCurrentTableUnavailable ? "destructive" : "secondary"} className="text-xs">
                                  Combinée{isCurrentTableUnavailable ? " - Indisponible" : ""}
                                </Badge>
                              )}
                              {!isCombined && isCurrentTableUnavailable && (
                                <Badge variant="destructive" className="text-xs">Indisponible</Badge>
                              )}
                            </div>
                          );
                        })()}
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {getAvailableTablesForDateTime.length === 0 ? (
                        <SelectItem value="no-tables" disabled>
                          Aucune table disponible
                        </SelectItem>
                      ) : (() => {
                        const personsCount = parseInt(numberOfPeople);
                        const renderSingle = (table: typeof getAvailableTablesForDateTime[number]) => {
                          const isOptimal = table.capacity === personsCount || table.capacity === personsCount + 1;
                          const isGood = table.capacity > personsCount && table.capacity <= personsCount + 2;
                          const isUnderCapacity = table.capacity < personsCount;
                          return (
                            <SelectItem key={table.id} value={table.id}>
                              <div className="flex items-center justify-between w-full gap-2">
                                <span>{table.name} - {table.capacity} pers.</span>
                                {isUnderCapacity && (
                                  <Badge variant="warning" className="text-[10px] px-1.5 py-0">Sous-capacité</Badge>
                                )}
                                {!isUnderCapacity && isOptimal && (
                                  <Badge variant="default" className="text-xs">Optimal</Badge>
                                )}
                                {!isUnderCapacity && !isOptimal && isGood && (
                                  <Badge variant="secondary" className="text-xs">Très bon</Badge>
                                )}
                              </div>
                            </SelectItem>
                          );
                        };
                        const renderCombined = (table: typeof getAvailableTablesForDateTime[number]) => {
                          const isUnbookable = (table as any).isBookable === false;
                          return (
                            <SelectItem key={table.id} value={table.id}>
                              <div className="flex items-center justify-between w-full gap-2">
                                <span>{table.name} - {table.capacity} pers.</span>
                                <div className="flex items-center gap-1">
                                  {isUnbookable && (
                                    <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-amber-400 text-amber-700 bg-amber-50" title="Hors réservation — assignation manuelle staff, invisible côté client">
                                      Hors résa
                                    </Badge>
                                  )}
                                  <Badge variant="outline" className="text-xs">Combinée</Badge>
                                </div>
                              </div>
                            </SelectItem>
                          );
                        };
                        const groups = groupTablesBySpace(getAvailableTablesForDateTime, editBookableZones);
                        const showSpace = groups.length > 1;
                        
                        return (
                          <>
                            {groups.map((group, gi) => {
                              const singles = group.items.filter(t => t.type === 'single');
                              const combos = group.items.filter(t => t.type === 'combined');
                              return (
                                <div key={group.key}>
                                  {showSpace && (
                                    <div className={`px-2 py-1.5 text-xs font-bold uppercase tracking-wide text-foreground border-b border-border ${gi > 0 ? 'mt-3' : ''}`}>
                                      {group.label}
                                    </div>
                                  )}
                                  {singles.length > 0 && (
                                    <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground">
                                      TABLES SIMPLES
                                    </div>
                                  )}
                                  {singles.map(renderSingle)}
                                  {combos.length > 0 && (
                                    <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground mt-2">
                                      TABLES COMBINÉES
                                    </div>
                                  )}
                                  {combos.map(renderCombined)}
                                </div>
                              );
                            })}
                          </>
                        );
                      })()}
                    </SelectContent>
                  </Select>
                </div>

            <div className="space-y-2">
                <Label htmlFor="status">Statut de la réservation</Label>
                <Select value={status} onValueChange={(value: "planifié" | "arrivé" | "annulé" | "en_attente") => setStatus(value)}>
                  <SelectTrigger id="status">
                    <SelectValue>
                      {status === "en_attente" && (
                        <div className="flex items-center gap-2">
                          <div className="w-2 h-2 rounded-full bg-amber-500"></div>
                          En attente
                        </div>
                      )}
                      {status === "planifié" && (
                        <div className="flex items-center gap-2">
                          <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                          Planifié
                        </div>
                      )}
                      {status === "arrivé" && (
                        <div className="flex items-center gap-2">
                          <div className="w-2 h-2 rounded-full bg-green-500"></div>
                          Arrivé
                        </div>
                      )}
                      {status === "annulé" && (
                        <div className="flex items-center gap-2">
                          <div className="w-2 h-2 rounded-full bg-red-500"></div>
                          Annulé
                        </div>
                      )}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="en_attente">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full bg-amber-500"></div>
                        En attente
                      </div>
                    </SelectItem>
                    <SelectItem value="planifié">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                        Planifié
                      </div>
                    </SelectItem>
                    <SelectItem value="arrivé">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full bg-green-500"></div>
                        Arrivé
                      </div>
                    </SelectItem>
                    <SelectItem value="annulé">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full bg-red-500"></div>
                        Annulé
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="notes">Note de réservation</Label>
                <Input
                  id="notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                   placeholder="Note de réservation"
                   maxLength={500}
                 />
                 <p className="text-xs text-muted-foreground">Note ajoutée à la réservation.</p>
              </div>

              {applicableQuestion && (
                <EstablishmentQuestionField
                  question={applicableQuestion}
                  answers={customAnswers}
                  onAnswersChange={setCustomAnswers}
                  staffMode
                  idPrefix="edit-reservation-question"
                />
              )}


              <div className="space-y-2">
                <Label htmlFor="internalNotes">Note interne</Label>
                <Textarea
                  id="internalNotes"
                  value={internalNotes}
                  onChange={(e) => setInternalNotes(e.target.value)}
                  placeholder="Notes internes pour le suivi de la réservation..."
                  maxLength={1000}
                  className="min-h-[100px]"
                />
                <p className="text-xs text-muted-foreground">Visible uniquement par l'équipe</p>
              </div>

              {/* Blacklist / Allergy alerts */}
              {(() => {
                const emailToCheck = reservationClient?.email;
                if (!emailToCheck) return null;
                const record = customerNotes.get(emailToCheck.toLowerCase());
                if (!record) return null;
                return (
                  <>
                    {record.isBlacklisted && (
                      <Alert className="border-destructive bg-destructive/10">
                        <AlertTriangle className="h-4 w-4 text-destructive" />
                        <AlertDescription className="text-destructive font-medium">
                          ⚠️ Ce client est blacklisté
                        </AlertDescription>
                      </Alert>
                    )}
                    {record.hasNoShow && (
                      <Alert className="border-amber-500 bg-amber-50 dark:bg-amber-950/20">
                        <AlertTriangle className="h-4 w-4 text-amber-600" />
                        <AlertDescription className="text-amber-700 dark:text-amber-400 font-medium">
                          Ce client a un historique de no-show
                        </AlertDescription>
                      </Alert>
                    )}
                    {record.allergiesTags && record.allergiesTags.length > 0 && (
                      <Alert className="border-orange-400 bg-orange-50 dark:bg-orange-950/20">
                        <AlertTriangle className="h-4 w-4 text-orange-600" />
                        <AlertDescription className="text-orange-700 dark:text-orange-400">
                          <span className="font-medium">Allergies :</span> {record.allergiesTags.join(', ')}
                          {record.allergiesNotes && <p className="text-xs mt-1">{record.allergiesNotes}</p>}
                        </AlertDescription>
                      </Alert>
                    )}
                  </>
                );
              })()}
              {/* Notes client persistantes */}
              {(() => {
                const emailToCheck = reservationClient?.email;
                const existingNotes = getNotesForEmail(emailToCheck);
                const customerId = getCustomerIdForEmail(emailToCheck);
                if (!emailToCheck || (!existingNotes && !editingCustomerNotes)) return null;
                return (
                  <div className="space-y-2 p-3 rounded-lg border-2 border-violet-300 dark:border-violet-700 bg-violet-50 dark:bg-violet-950/20">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <StickyNote className="h-4 w-4 text-violet-600 dark:text-violet-400" />
                        <Label className="text-violet-700 dark:text-violet-300 font-medium">Notes client (persistantes)</Label>
                      </div>
                      {!editingCustomerNotes && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-7 text-violet-600 dark:text-violet-400 hover:text-violet-800"
                          onClick={() => {
                            setEditingCustomerNotes(true);
                            setCustomerNotesValue(existingNotes || "");
                          }}
                        >
                          <Pencil className="h-3 w-3 mr-1" />
                          Modifier
                        </Button>
                      )}
                    </div>
                    {editingCustomerNotes ? (
                      <div className="space-y-2">
                        <Textarea
                          value={customerNotesValue}
                          onChange={(e) => setCustomerNotesValue(e.target.value)}
                          placeholder="Préférences, allergies, notes importantes..."
                          className="min-h-[60px] border-violet-300 dark:border-violet-700"
                          maxLength={1000}
                        />
                        <div className="flex gap-2 justify-end">
                          <Button type="button" variant="ghost" size="sm" onClick={() => setEditingCustomerNotes(false)}>
                            Annuler
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            disabled={savingCustomerNotes}
                            onClick={async () => {
                              if (!customerId || !ctxEstablishmentId) return;
                              setSavingCustomerNotes(true);
                              try {
                                const { error } = await supabase
                                  .from('customers')
                                  .update({ notes: customerNotesValue || null })
                                  .eq('id', customerId)
                                  .eq('establishment_id', ctxEstablishmentId);
                                if (error) throw error;
                                await refetchCustomerNotes();
                                setEditingCustomerNotes(false);
                                toast({ title: "Notes client mises à jour" });
                              } catch {
                                toast({ title: "Erreur", description: "Impossible de sauvegarder.", variant: "destructive" });
                              } finally {
                                setSavingCustomerNotes(false);
                              }
                            }}
                          >
                            {savingCustomerNotes ? "..." : "Enregistrer"}
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <p className="text-sm text-violet-800 dark:text-violet-200 whitespace-pre-wrap">{existingNotes}</p>
                    )}
                    <p className="text-xs text-violet-500">Ces notes s'appliquent à toutes les réservations de ce client</p>
                  </div>
                );
              })()}

              {/* Source de la réservation */}
              <div className="space-y-2">
                <Label htmlFor="editSource">Source de la réservation</Label>
                <Select value={source || "none"} onValueChange={(val) => setSource(val === "none" ? "" : val)}>
                  <SelectTrigger id="editSource">
                    <SelectValue placeholder="Sélectionner une source" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Non renseignée</SelectItem>
                    <SelectItem value="Google">Google</SelectItem>
                    <SelectItem value="Site web">Site web</SelectItem>
                    <SelectItem value="Instagram">Instagram</SelectItem>
                    <SelectItem value="Facebook">Facebook</SelectItem>
                    <SelectItem value="TheFork">TheFork</SelectItem>
                    <SelectItem value="Téléphone">Téléphone</SelectItem>
                    <SelectItem value="Passage">Passage</SelectItem>
                    <SelectItem value="Bouche à oreille">Bouche à oreille</SelectItem>
                    <SelectItem value="Widget">Widget (réservation en ligne)</SelectItem>
                    <SelectItem value="Autre">Autre</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  D'où vient ce client ? (Google, réseaux sociaux, téléphone, etc.)
                </p>
              </div>
              {isTimeMode && (
                <div className="space-y-2">
                  <Label htmlFor="editCustomDuration">Durée de la réservation</Label>
                  <Select
                    value={customDuration ? customDuration.toString() : "default"}
                    onValueChange={(val) => setCustomDuration(val === "default" ? null : parseInt(val))}
                  >
                    <SelectTrigger id="editCustomDuration">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="default">Par défaut ({reservationDurationMinutes} min)</SelectItem>
                      <SelectItem value="30">30 min</SelectItem>
                      <SelectItem value="45">45 min</SelectItem>
                      <SelectItem value="60">1h</SelectItem>
                      <SelectItem value="75">1h15</SelectItem>
                      <SelectItem value="90">1h30</SelectItem>
                      <SelectItem value="105">1h45</SelectItem>
                      <SelectItem value="120">2h</SelectItem>
                      <SelectItem value="150">2h30</SelectItem>
                      <SelectItem value="180">3h</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    Modifier la durée permet d'optimiser l'utilisation des tables.
                  </p>
                </div>
              )}
            </div>

            {/* Client Assignment Section */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-sm">Client assigné</h3>
                {reservationClient && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setEditingClient(true)}
                  >
                    <Edit className="w-4 h-4 mr-2" />
                    Modifier le client
                  </Button>
                )}
              </div>

              {reservationClient ? (
                <div className="space-y-3">
                  {/* Client notes banner */}
                  {reservationClient.notes && (
                    <Alert className="border-amber-500/50 bg-amber-50 dark:bg-amber-950/20">
                      <AlertTriangle className="h-4 w-4 text-amber-600" />
                      <AlertDescription className="text-amber-800 dark:text-amber-200">
                        <p className="font-medium mb-1">Note client :</p>
                        <p className="whitespace-pre-wrap text-sm">{reservationClient.notes}</p>
                      </AlertDescription>
                    </Alert>
                  )}
                  
                  <Card className="cursor-pointer hover:border-primary/50 transition-colors" onClick={() => setEditingClient(true)}>
                    <CardContent className="pt-6">
                      <div className="flex items-start gap-4">
                        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                          <User className="w-5 h-5 text-primary" />
                        </div>
                        <div className="flex-1 space-y-2">
                          <div>
                            <p className="font-semibold text-base">
                              {formatCustomerName(reservationClient.firstName, reservationClient.lastName)}
                            </p>
                            <p className="text-sm text-muted-foreground">
                              {reservationClient.totalReservations} réservation{reservationClient.totalReservations > 1 ? 's' : ''} au total
                            </p>
                          </div>
                          <div className="space-y-1 text-sm">
                            {reservationClient.email && (
                              <p className="flex items-center gap-2">
                                <span className="text-muted-foreground">Email:</span>
                                <span>{reservationClient.email}</span>
                              </p>
                            )}
                            <p className="flex items-center gap-2">
                              <span className="text-muted-foreground">Téléphone:</span>
                              <span>{reservationClient.phone}</span>
                            </p>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              ) : (
                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    Client non trouvé dans la base de données. Les informations: {reservation.name}{reservation.email ? ` - ${reservation.email}` : ''}
                  </AlertDescription>
                </Alert>
              )}
            </div>
          </div>

        </form>

        <DialogFooter className="shrink-0 border-t pt-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            Annuler
          </Button>
          <Button
            type="submit"
            form="edit-reservation-form"
            disabled={
              (!selectedTableId && status !== 'en_attente' && status !== 'annulé') ||
              !selectedDate ||
              !selectedTimeSlot ||
              numberOfPeople === "" ||
              parseInt(numberOfPeople) < 1 ||
              isCurrentTableUnavailable
            }
          >
            Enregistrer les modifications
          </Button>
        </DialogFooter>
      </DialogContent>

      {/* Edit Client Dialog */}
      {reservationClient && (
        <EditClientDialog
          open={editingClient}
          onOpenChange={setEditingClient}
          client={reservationClient}
          onClientUpdate={onClientUpdate}
        />
      )}
    </Dialog>
  );
}
